import { useCallback, useEffect, useMemo, useState } from 'react'
import type { StepNode } from '@/api/types'

/**
 * Outline-row expansion state. Unified semantic for both kinds of step:
 *
 * - SECTION — "expanded" means its children render.
 * - ACTION  — "expanded" means its details row renders (description, photos, …).
 *
 * Default initial state: every step expanded (sections show children, actions show their
 * details row). Users can collapse selectively or via the collapse-all toolbar action.
 *
 * State is per-project and persisted via the `projectId` parameter so reopening a project
 * brings back the same expanded/collapsed shape.
 */
const STORAGE_PREFIX = 'todone.project.outline.expanded.'

const collectIds = (
  steps: StepNode[],
  kind: 'SECTION' | 'ACTION' | 'PROJECT_REFERENCE' | 'all'
): string[] => {
  const out: string[] = []
  const walk = (s: StepNode) => {
    if (kind === 'all' || s.kind === kind) out.push(s.id)
    s.children.forEach(walk)
  }
  steps.forEach(walk)
  return out
}

const readStored = (projectId: string, fallback: Set<string>): Set<string> => {
  if (typeof window === 'undefined') return fallback
  const raw = window.localStorage.getItem(STORAGE_PREFIX + projectId)
  if (!raw) return fallback
  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed))
      return new Set(parsed.filter((x): x is string => typeof x === 'string'))
  } catch {
    /* fall through */
  }
  return fallback
}

export interface OutlineExpansion {
  isExpanded: (stepId: string) => boolean
  toggle: (stepId: string) => void
  expandAll: () => void
  collapseAll: () => void
  /** Add every ACTION id to the expanded set without touching SECTION state. */
  expandAllActions: () => void
  /** Remove every ACTION id from the expanded set without touching SECTION state. */
  collapseAllActions: () => void
}

export const useOutlineExpansion = (projectId: string, steps: StepNode[]): OutlineExpansion => {
  // Every step open by default — sections show children, actions show details.
  const defaultExpanded = useMemo(() => new Set(collectIds(steps, 'all')), [steps])
  const [expanded, setExpanded] = useState<Set<string>>(() =>
    readStored(projectId, defaultExpanded)
  )

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(STORAGE_PREFIX + projectId, JSON.stringify([...expanded]))
  }, [expanded, projectId])

  const isExpanded = useCallback((stepId: string) => expanded.has(stepId), [expanded])
  const toggle = useCallback((stepId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(stepId)) next.delete(stepId)
      else next.add(stepId)
      return next
    })
  }, [])
  const expandAll = useCallback(() => {
    setExpanded(new Set(collectIds(steps, 'all')))
  }, [steps])
  const collapseAll = useCallback(() => {
    setExpanded(new Set())
  }, [])
  const expandAllActions = useCallback(() => {
    setExpanded((prev) => {
      const next = new Set(prev)
      collectIds(steps, 'ACTION').forEach((id) => next.add(id))
      return next
    })
  }, [steps])
  const collapseAllActions = useCallback(() => {
    setExpanded((prev) => {
      const actionIds = new Set(collectIds(steps, 'ACTION'))
      const next = new Set<string>()
      prev.forEach((id) => {
        if (!actionIds.has(id)) next.add(id)
      })
      return next
    })
  }, [steps])

  return { isExpanded, toggle, expandAll, collapseAll, expandAllActions, collapseAllActions }
}
